<script lang="ts">
  import * as H from "../settings/hints.ts";
  import { fill } from "../settings/index.ts";
  import * as A from "../settings/amounts.ts";
  import BalanceAmount from "../components/BalanceAmount.svelte";
  import {
    collectedByMonth,
    firstActivity,
    runningBy,
    monthsBetween,
    owedTrend,
    rangeBreakdowns,
    trendDates,
    groupSpendMicro,
    memberSpendByMonth,
    groupReceivedMicro,
    currentOwedMicro,
    groupPurchaseSpan,
    owedToYouMicro,
    youOweMicro,
    type DateRange,
    type Dimension,
    shownCents,
  } from "@split/core";
  import RangePicker from "../components/RangePicker.svelte";
  import { app } from "../lib/store.svelte.ts";
  import { colorOf } from "../lib/colors.ts";
  import { formatMicro, monthLabel, shortDate } from "../lib/format.ts";
  import BarPanels, { type Section } from "../components/BarPanels.svelte";
  import LineChart from "../components/charts/LineChart.svelte";
  import ColumnChart from "../components/charts/ColumnChart.svelte";

  const L = $derived(app.ledger);
  const S = $derived(app.viewState);
  const colorFor = (name: string) => {
    const p = Object.values(S.people).find((x) => x.name === name);
    return p ? colorOf(p.color) : "var(--ink-2)";
  };
  /** A member sees only their own part of everything. */
  const who = $derived(app.memberSession?.personId);
  const spend = $derived(groupSpendMicro(L, who));

  const trend = $derived.by(() => {
    const start = firstActivity(S, L);
    if (!start) return [];
    let ends = trendDates(start, app.today);
    if (ends.length > 104) ends = ends.slice(-104);
    const pts = owedTrend(S, L, ends, who).map((p) => ({ label: shortDate(p.date), value: p.valueMicro }));
    const now = currentOwedMicro(L, who);
    if (pts.length) pts[pts.length - 1]!.value = now;
    return pts;
  });

  const months = $derived.by(() => {
    const first = L.insights.byMonth[0]?.label;
    if (!first) return [];
    const m = monthsBetween(first, app.today.slice(0, 7));
    return m.slice(-18);
  });
  const monthSeries = $derived.by(() => {
    const spent = who ? memberSpendByMonth(L, who) : new Map(L.insights.byMonth.map((b) => [b.label, b.totalMicro]));
    const came = collectedByMonth(S);
    return [
      { name: "Spent", color: "var(--series-1)", values: months.map((m) => spent.get(m) ?? 0) },
      { name: "Came in", color: "var(--series-3)", values: months.map((m) => came.get(m) ?? 0) },
    ];
  });

  // Breakdowns for the range you pick, each with a month-by-month graph.
  let range = $state<DateRange>({ from: null, to: null });
  /** First and last purchase dates in this group: the range slider's ends. */
  const dataSpan = $derived(groupPurchaseSpan(S, firstActivity(S, L)));
  const B = $derived(rangeBreakdowns(S, L, range, who));

  // The same tiles as the main Overview, for this group only (straight from the ledger).
  const owedToYou = $derived(owedToYouMicro(L));
  const youOwe = $derived(youOweMicro(L));
  const mine = $derived(who ? (L.byPersonId[who]?.balanceMicro ?? 0) : 0);
  const thisMonth = $derived(monthSeries[0]?.values.at(-1) ?? 0);
  const last30 = $derived(groupReceivedMicro(S, app.today, 30, who));
  const short = (m: string) => monthLabel(m).slice(0, 3);
  /** Running totals over the range, as lines (days, weeks or months depending on its length). */
  function graph(dim: Dimension, colorBy?: (name: string) => string) {
    const r = runningBy(S, L, range, dim, app.today, 5, who);
    const series = r.series.map((x, k, all) => ({
      name: x.name,
      color: k === all.length - 1 && x.name.endsWith("more") ? "var(--muted)" : (colorBy?.(x.name) ?? `var(--series-${k + 1})`),
      values: x.values,
    }));
    const labels = r.starts.map((d) => (r.unit === "month" ? short(d.slice(0, 7)) : shortDate(d)));
    return { labels, series };
  }
  const sections = $derived<Section[]>(
    who
      ? [
          { title: "Who you split with", rows: B.byPerson, color: colorFor, graph: graph("person", colorFor) },
          { title: "By store", rows: B.byStore, graph: graph("store") },
          { title: "By category", rows: B.byCategory, graph: graph("category") },
        ]
      : [
          { title: "By person", rows: B.byPerson, color: colorFor, graph: graph("person", colorFor) },
          { title: "By store", rows: B.byStore, graph: graph("store") },
          { title: "By category", rows: B.byCategory, graph: graph("category") },
          { title: "By card", rows: B.byCard, graph: graph("card") },
        ],
  );
  const lede = $derived(fill(who ? H.MEMBER_OVERVIEW_LEDE : H.GROUP_OVERVIEW_LEDE, { amount: formatMicro(spend), trips: L.trips.length }));
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>Overview</h1>
      {#if lede.trim()}<p class="lede">{lede}</p>{/if}
    </div>
  </div>
  <div class="tiles">
    {#if who}
      <div class="tile"><span class="muted">{A.MY_BALANCE_LABEL}</span><strong class="num"><BalanceAmount cents={shownCents(mine)} /></strong></div>
      <div class="tile"><span class="muted">You paid, last 30 days</span><strong class="num">{formatMicro(last30)}</strong></div>
      <div class="tile"><span class="muted">Your part this month</span><strong class="num">{formatMicro(thisMonth)}</strong></div>
    {:else}
      <div class="tile"><span class="muted">{A.TOTAL_OWED_LABEL}</span><strong class="num">{formatMicro(owedToYou)}</strong></div>
      <div class="tile"><span class="muted">{A.TOTAL_OWE_LABEL}</span><strong class="num">{formatMicro(youOwe)}</strong></div>
      <div class="tile"><span class="muted">Came in, last 30 days</span><strong class="num">{formatMicro(last30)}</strong></div>
      <div class="tile"><span class="muted">Spent this month</span><strong class="num">{formatMicro(thisMonth)}</strong></div>
    {/if}
  </div>
  <div class="charts">
    <div class="panel"><LineChart title={who ? A.CHART_MINE_TITLE : A.CHART_OWED_TITLE} points={trend} /></div>
    <div class="panel">
      <ColumnChart title={who ? "Your part each month" : "Spent each month"} labels={months.map((m) => monthLabel(m).slice(0, 3))} series={monthSeries.slice(0, 1)} />
    </div>
    <div class="panel">
      <ColumnChart title={who ? "You paid each month" : "Came in each month"} labels={months.map((m) => monthLabel(m).slice(0, 3))} series={monthSeries.slice(1)} />
    </div>
  </div>
  <div class="range-row">
    <div class="rh">
      <h2>Breakdowns</h2>
      <span class="muted">{formatMicro(B.totalMicro)} in this range</span>
    </div>
    <div class="rp"><RangePicker key="insights" bind:range min={dataSpan.lo} max={dataSpan.hi} /></div>
  </div>
  <BarPanels {sections} />
</div>

<style>
  .tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
  }
  .tile {
    display: grid;
    gap: 4px;
    padding: 14px 16px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
    font-size: var(--t-sm);
  }
  .tile strong {
    font-size: var(--t-xl);
  }
  .range-row {
    display: grid;
    gap: 10px;
    margin: 8px 0 14px;
  }

  .rh {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    align-items: baseline;
  }

  .rp {
    max-width: 560px;
  }

  .range-row h2 {
    margin: 0;
    font-size: var(--t-lg);
  }

  .charts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
  }

  .panel {
    padding: 16px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
    min-width: 0;
  }
</style>
